import { useCallback } from 'react';
import { pick, prop } from 'ramda';
import { useTranslations } from 'js/hooks/i18n';
import { useUserSelector } from 'js/store/hooks';
import { useFormatter } from 'js/hooks/useFormatter';
import { formatCurrency } from 'js/components/common/Fields/LimitEdit/helpers';
import { TFormField, TFormFieldRadioButton } from 'js/components/common/Form/types';
import { FORM_FIELD_TYPE } from 'js/components/common/Form/constants';

import * as constants from '../constants';

export const useGetMessageHtml = ({
    tkPrefix,
    daydepositlimit,
    weekdepositlimit,
    monthdepositlimit
}: {
    tkPrefix?: string;
    daydepositlimit: string;
    weekdepositlimit: string;
    monthdepositlimit: string;
}) => {
    const translate = useTranslations(tkPrefix);
    const currencyCode = useUserSelector(prop('currencyCode'));
    const formatter = useFormatter();
    const currency = formatCurrency({
        formatter,
        tt: translate,
        currencyCode,
        precision: 0,
        unlimitedValue: constants.DEPOSIT_LIMIT_UNLIMITED_VALUE_STR
    });

    const {
        userDetails: {
            firstName,
            lastName,
            fullFirstname,
            fullFirstSurname,
            fullSecondSurname,
            email
        }
    } = useUserSelector(pick(['userDetails']));

    return useCallback(({
        dataMap,
        fields
    }: {
        dataMap: any,
        fields: TFormField[]
    }) => {
        const head = `${translate('user')}:
        ${translate('firstName')}: ${firstName}
        ${translate('lastName')}: ${lastName}
        ${translate('fullFirstname')}: ${fullFirstname ?? ''}
        ${translate('fullFirstSurname')}: ${fullFirstSurname ?? ''}
        ${translate('fullSecondSurname')}: ${fullSecondSurname ?? ''}
        <br />${translate('email')}: <a href="mailto:${email}">${email}</a>`;

        const footer = `<br />
        <br /><br />${translate('dailylimit')}: = ${currency(daydepositlimit)}
        <br /><br />${translate('weeklylimit')}: = ${currency(weekdepositlimit)}
        <br /><br />${translate('monthlylimit')}: = ${currency(monthdepositlimit)}`;

        const messages = fields
            .filter((field): field is TFormFieldRadioButton => {
                if (field.type === FORM_FIELD_TYPE.RADIO_BUTTON) {
                    return Boolean(field.imsField);
                }

                return false;
            })
            .map(({ label, imsField = '' }, index) =>
                `<br />${translate('question')} ${index + 1} - ${translate(label)} - ${dataMap[imsField]}`);

        return `${head}${messages}${footer}`;
    }, [
        translate,
        currency,
        daydepositlimit,
        weekdepositlimit,
        monthdepositlimit,
        firstName,
        lastName,
        fullFirstname,
        fullFirstSurname,
        fullSecondSurname,
        email
    ]);
};
